import { useEffect, useState } from 'react';
import Icon from '@/icons';
import { Team } from 'types';
import { ChangeTeamAvatar } from './Overview/ChangeAvatar';
import permissions from '@/permissions';
import { ChangeBanner } from './Overview/ChangeBanner';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

export const SettingsTeamOverview = ({  setTeamData, me }: { setTeamData: any, me: any }) => {
    const queryClient = useQueryClient();
    const {teamId} = useParams();
    const teamData = queryClient.getQueryData<any>(['team', teamId]).team as Team;
    const [nameChanged, setNameChanged] = useState(false);
    const [name, setName] = useState(teamData.name);
    const [nameError, setNameError] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [description, setDescription] = useState(teamData.description);
    const handleNameChange = () => {
        fetch(`__SITE_BASE__/api/teams/${teamData.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name }),
        });
    };

    const handleDescriptionChange = () => {
        fetch(`__SITE_BASE__/api/teams/${teamData.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ description }),
        });
    };

    useEffect(() => {
        if (name?.length < 3 || name?.length > 20) {
            setNameError(true);
            setButtonDisabled(true);
        } else {
            setNameError(false);
            setButtonDisabled(false);
        }
    }, [name]);

    const changeBannerOnTeamData = (url: string) => {
        setTeamData({ ...teamData, banner: url });
    };

    return (
        <div className="flex flex-col items-start w-full focus:outline-none space-y-4">

            <div className='flex items-center w-full mt-2'>
                <div className='flex flex-col items-start gap-2'>
                    <div className="text-white h-full w-auto">
                        <ChangeTeamAvatar teamData={teamData} setTeamData={setTeamData}>
                            <div className="rounded-lg relative group">
                                <div className='flex items-center justify-center cursor-pointer h-20 w-20 bg-brand-darkest rounded-lg'>
                                    <div className="rounded-lg relative group items-center">
                                        {teamData.profilePicture ? (
                                            <img
                                                src={teamData.profilePicture}
                                                alt={`${teamData.name} logo`}
                                                className={`h-20 rounded-lg transition-all duration-200`}
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center w-16 h-12 rounded-lg text-white text-x font-bold">
                                                {teamData?.name?.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                </div>


                                <div className='hidden group-hover:flex absolute -bottom-0 h-full w-full items-center justify-center bg-brand-darkest cursor-pointer bg-opacity-50 rounded-lg'>
                                    <div className="bg-brand-dark text-gray-300 rounded-lg">
                                        <Icon name="picture" size='16' />
                                    </div>
                                </div>
                            </div>
                        </ChangeTeamAvatar>
                    </div>

                </div>

                <div className="flex flex-col items-start ml-4 gap-2 w-full">
                    <h1 className="text-white font text-sm w-full">
                        Team Name
                    </h1>
                    {nameError && (
                        <div className="text-red-500 text-sm w-full">
                            <p>Team name must be between 3 and 20 characters</p>
                        </div>
                    )}
                    <div className='flex items-center gap-2'>
                        <input
                            type="text"
                            className="bg-brand-darkest w-64 sm:w-96 text-white rounded-lg p-2 focus:outline-none disabled:bg-brand-dark disabled:cursor-not-allowed"
                            value={name}
                            minLength={3}
                            maxLength={12}
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameChanged(true);
                            }}
                            disabled={
                                !Boolean(BigInt(me ?? '0') & permissions.get("MANAGE_TEAM" as string)!)
                            }
                        />

                        {nameChanged && (
                            <div className="flex items-center gap-2">
                                <button
                                    className="bg-brand-green px-5 py-2 w-full rounded-lg text-white font-semibold hover:bg-green-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                                    disabled={buttonDisabled}
                                    onClick={() => {
                                        setTeamData({ ...teamData, name });
                                        setNameChanged(false);
                                        handleNameChange();
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className='flex flex-col items-start w-full gap-2 relative'>
                <h1 className="text-gray-300 font-semibold">
                    Team Banner
                </h1>
                <div className="relative w-full group rounded-lg overflow-hidden">
                    {teamData.banner ? (
                        <img
                            src={teamData.banner}
                            draggable="false"
                            className="object-cover rounded-lg transition-all duration-300 h-32 w-full"
                        />
                    ) : (
                        <div className="w-full h-32 bg-brand-dark rounded-lg flex items-center justify-center text-white" />
                    )}
                    <ChangeBanner teamId={teamData.id} ChangeBanner={changeBannerOnTeamData}>
                        <div className="hidden top-0 group-hover:flex w-full h-32 justify-center rounded-lg transition-all gap-2 items-center z-10 absolute text-white hover:bg-brand-profile/50 duration-300 px-2 py-1">
                            <span className="absolute top-0 w-full h-full items-center justify-center flex">
                                <div className='bg-brand-dark text-gray-300 p-2 rounded-lg'>
                                    <Icon name="picture" size="24" />
                                </div>
                            </span>
                        </div>
                    </ChangeBanner>
                </div>

            </div>

            <hr className="w-full border-gray-500" />

            <div className='flex flex-col items-start w-full gap-2 relative'>
                <label className="text-gray-300 font-semibold">
                    Description
                </label>
                <textarea
                    className="w-full rounded-lg h-32 px-2 py-4 border-brand-green border-2 border-opacity-50 hover:border-opacity-100 transition-all duration-200 overflow-y-hidden bg-brand-dark text-gray-300 focus:outline-none resize-none leading-none"
                    placeholder="What's your team about?"
                    value={description}
                    maxLength={1000}
                    onChange={(e) => {
                        setDescription(e.target.value)
                    }}
                />

                <div className='flex items-start relative w-full justify-start'>

                    {description !== teamData.description && (
                        <button
                            className="bg-brand-green  px-5 py-2 w-64 rounded-lg text-white font-semibold hover:bg-green-500 disabled:opacity-50"
                            disabled={buttonDisabled}
                            onClick={() => {
                                setTeamData({ ...teamData, description });
                                handleDescriptionChange();
                            }}
                        >
                            Save
                        </button>
                    )}

                    <div className='items-end absolute w-full flex justify-end'>
                        <p className="text-gray-300 text-sm">
                            {description?.length}/1000
                        </p>
                    </div>

                </div>
            </div>
        </div>
    );
};

